<script>
import StageProgress from '@/views/dashboards/widgets/StageProgress.vue';
import StaffDashboardUser from '@/views/dashboards/widgets/StaffDashboardUser.vue';
import {mapGetters} from 'vuex';
import {UserRole} from '@/store/modules/users';

export default {
   name: 'StaffDashboardTableRow',
   computed: {
      ...mapGetters(['isExternalPartner']),
      ...mapGetters('users', {userMap: 'userMap', usersLoading: 'loading'}),
      UserRole() {
         return UserRole;
      },
      localBusinessManagers() {
         return this.resolveMembers(this.businessManagers, UserRole.BUSINESS_MANAGER);
      },
      localProjectManagers() {
         return this.resolveMembers(this.projectManagers, UserRole.PROJECT_MANAGER);
      },
      localProjectAssociates() {
         return this.resolveMembers(this.projectAssociates, UserRole.PROJECT_ASSOCIATE);
      },
      allUsers() {
         return [
            ...this.localBusinessManagers,
            ...this.localProjectManagers,
            ...this.localProjectAssociates,
         ].map((member) => member.email);
      },
      loadingStudyTeamMembers() {
         return this.usersLoading;
      },
      loadingProjectTeamMembers() {
         return this.usersLoading;
      },
      isFilingDeadlineWithinTwoWeeks() {
         if (!this.filingDeadlineMonth || !this.filingDeadlineDay) return false;
         const now = new Date();
         now.setHours(0, 0, 0, 0);
         const msInDay = 24 * 60 * 60 * 1000;
         return [-1, 0, 1].some((yearOffset) => {
            const deadline = new Date(
               now.getFullYear() + yearOffset,
               this.filingDeadlineMonth - 1,
               this.filingDeadlineDay
            );
            return Math.abs((now - deadline) / msInDay) <= 14;
         });
      },
   },
   components: {StaffDashboardUser, StageProgress},
   props: {
      companyName: {
         type: String,
      },
      payrollTaxElection: {
         type: Boolean,
         default: false,
      },
      filingDeadlineMonth: {
         type: Number,
         default: null,
      },
      filingDeadlineDay: {
         type: Number,
         default: null,
      },
      id: {
         type: Number,
      },
      studies: {
         type: Array,
         default: function () {
            return [];
         },
      },
      businessManagers: {
         type: Array,
         default: function () {
            return [];
         },
      },
      projectManagers: {
         type: Array,
         default: function () {
            return [];
         },
      },
      projectAssociates: {
         type: Array,
         default: function () {
            return [];
         },
      },
      studyTeamMembers: {
         type: Array,
         default: function () {
            return [];
         },
      },
   },
   data() {
      return {
         show: false,
         activeAction: false,
      };
   },
   methods: {
      formatMonthDay: function (month, day) {
         if (!month || !day) return 'NA';
         const date = new Date(2000, month - 1, day);

         return date.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
         });
      },

      resolveMembers(memberIds, type) {
         return (memberIds || []).map((id) => {
            const profile = this.userMap[id];
            return {
               id,
               email: profile?.email,
               name: profile ? `${profile.firstName} ${profile.lastName}` : 'Unknown Name',
               type,
            };
         });
      },

      openEmailClient(selectedEmail) {
         const toEmail = selectedEmail;
         const ccEmails = this.allUsers.filter((email) => email !== selectedEmail).join(',');

         window.location.href = `mailto:${toEmail}?cc=${ccEmails}`;
      },
   },
};
</script>

<template>
   <div
      v-if="!show"
      @click="show = !show"
      class="table-row d-flex border-bottom align-items-center"
      style="cursor: pointer"
      role="row"
      :aria-rowindex="1"
   >
      <div
         role="cell"
         class="d-flex align-items-center"
         style="min-width: 16.25rem; max-width: 16.25rem"
      >
         <p class="m-0 pl-3 pr-2">
            <span>
               <b-icon-chevron-right @click="show = !show" class="icon"></b-icon-chevron-right>
            </span>
         </p>
         <p class="m-0" style="font-size: 0.75rem">{{ companyName }}</p>
      </div>
      <div v-if="studies.length > 0" class="px-3 studies-wrapper" role="cell" style="width: 100%">
         <div v-for="study in studies" :key="study.id">
            <p class="" style="font-size: 0.5625rem" v-if="studies.length > 1">{{ study.label }}</p>
            <StageProgress :active-stage="study.stage"></StageProgress>
         </div>
      </div>
      <div v-else class="px-3" role="cell" style="width: 100%">
         <p class="m-0 no-open-study">No Open Study</p>
      </div>
      <div
         class="col deadline"
         role="cell"
         :style="`min-width: 145px; line-height: 40px; ${
            isFilingDeadlineWithinTwoWeeks ? 'color: #D33C3C;' : ''
         }`"
      >
         {{ formatMonthDay(filingDeadlineMonth, filingDeadlineDay) }}
      </div>
   </div>
   <div
      v-else
      class="
         is-open
         d-flex
         justify-content-between
         border-bottom
         align-items-baseline
         position-relative
      "
      role="row"
      :aria-rowindex="1"
   >
      <div class="pt-2 pb-4 col" role="cell" style="max-width: 16.25rem">
         <b-row>
            <b-col style="width: 16.25rem" class="pr-2 d-flex align-items-center">
               <p class="m-0" style="font-size: 1rem; font-weight: 700">
                  <span>
                     <b-icon-chevron-down
                        @click="show = !show"
                        class="icon icon--open"
                     ></b-icon-chevron-down>
                  </span>
               </p>
               <p class="m-0" style="font-size: 1rem; font-weight: 700">{{ companyName }}</p>
            </b-col>
         </b-row>
         <b-row class="pl-5">
            <b-col>
               <p class="label">Payroll Tax Election:</p>
               <p class="description">{{ payrollTaxElection ? 'Yes' : 'No' }}</p>
            </b-col>
         </b-row>
         <b-row v-if="!isExternalPartner" class="pl-5">
            <b-col class="pt-5">
               <b-button
                  variant="primary"
                  block
                  :to="{name: 'company-detail', params: {id}}"
                  class="view-client-details"
               >
                  View Client Details
               </b-button>
            </b-col>
         </b-row>
      </div>
      <div class="pt-2 pb-4 col" role="cell" v-if="studies.length !== 0">
         <div v-for="study in studies" :key="study.id">
            <b-row>
               <b-col cols="2">
                  <p class="label">Study Label</p>
                  <p class="description">{{ study.label }}</p>
               </b-col>
               <b-col cols="4">
                  <p class="label">Scope</p>
                  <p class="description">
                     {{ study.speriods.map((period) => period.label).join(', ') }}
                  </p>
               </b-col>
            </b-row>
            <StageProgress :is-open="true" :active-stage="study.stage"></StageProgress>
            <b-row class="pt-2" v-if="study.stage === 'CLIENT_DATA_COLLECTION'">
               <b-col>
                  <p class="stage-completion">Stage Completion {{ study.stageCompletion }}%</p>
               </b-col>
            </b-row>
            <hr />
         </div>
         <b-row>
            <b-col cols="4">
               <div class="loading-container">
                  <strong class="team-label loading-text">Client Team</strong>
                  <span v-if="loadingStudyTeamMembers" class="loading-spinner"></span>
               </div>
               <p v-if="studyTeamMembers.length === 0" class="description">NA</p>
               <div class="d-flex flex-wrap client-team">
                  <StaffDashboardUser
                     v-for="studyUser in studyTeamMembers"
                     :key="studyUser"
                     :id="studyUser"
                  ></StaffDashboardUser>
               </div>
            </b-col>
            <b-col cols="8">
               <div class="loading-container">
                  <strong class="team-label loading-text">Project Team</strong>
                  <span v-if="loadingProjectTeamMembers" class="loading-spinner"></span>
               </div>

               <p
                  v-if="
                     businessManagers.length === 0 &&
                     projectManagers.length === 0 &&
                     projectAssociates.length === 0
                  "
                  class="description"
               >
                  NA
               </p>
               <div class="d-flex flex-wrap project-members">
                  <StaffDashboardUser
                     v-for="businessManager in localBusinessManagers"
                     :key="businessManager.id"
                     :id="businessManager.id"
                     :type="UserRole.BUSINESS_MANAGER"
                     :email="businessManager.email"
                     @email-selected="openEmailClient"
                  ></StaffDashboardUser>
                  <StaffDashboardUser
                     v-for="projectManager in localProjectManagers"
                     :key="projectManager.id"
                     :id="projectManager.id"
                     :type="UserRole.PROJECT_MANAGER"
                     :email="projectManager.email"
                     @email-selected="openEmailClient"
                  ></StaffDashboardUser>
                  <StaffDashboardUser
                     v-for="projectAssociate in localProjectAssociates"
                     :key="projectAssociate.id"
                     :id="projectAssociate.id"
                     :type="UserRole.PROJECT_ASSOCIATE"
                     :email="projectAssociate.email"
                     @email-selected="openEmailClient"
                  ></StaffDashboardUser>
               </div>
            </b-col>
         </b-row>
      </div>
      <div class="pt-2 pb-4 col" role="cell" v-else>
         <b-row>
            <b-col cols="2">
               <p class="label">Study Label</p>
               <p class="description">NA</p>
            </b-col>
            <b-col cols="2">
               <p class="label">Scope</p>
               <p class="description">NA</p>
            </b-col>
         </b-row>
         <p class="no-open-study">No Open Study</p>
         <hr />
         <b-row>
            <b-col cols="4">
               <strong class="team-label">Client Team</strong>
               <p v-if="studyTeamMembers.length === 0" class="description">NA</p>
               <div class="d-flex flex-wrap client-team">
                  <StaffDashboardUser
                     v-for="studyUser in studyTeamMembers"
                     :key="studyUser"
                     :id="studyUser"
                  ></StaffDashboardUser>
               </div>
            </b-col>
            <b-col cols="8">
               <strong class="team-label">Project Team</strong>
               <p
                  v-if="
                     businessManagers.length === 0 &&
                     projectManagers.length === 0 &&
                     projectAssociates.length === 0
                  "
                  class="description"
               >
                  NA
               </p>
               <div class="d-flex flex-wrap project-members">
                  <StaffDashboardUser
                     v-for="businessManager in localBusinessManagers"
                     :key="businessManager.id"
                     :id="businessManager.id"
                     :type="UserRole.BUSINESS_MANAGER"
                     :email="businessManager.email"
                     @email-selected="openEmailClient"
                  ></StaffDashboardUser>
                  <StaffDashboardUser
                     v-for="projectManager in localProjectManagers"
                     :key="projectManager.id"
                     :id="projectManager.id"
                     :type="UserRole.PROJECT_MANAGER"
                     :email="projectManager.email"
                     @email-selected="openEmailClient"
                  ></StaffDashboardUser>
                  <StaffDashboardUser
                     v-for="projectAssociate in localProjectAssociates"
                     :key="projectAssociate.id"
                     :id="projectAssociate.id"
                     :type="UserRole.PROJECT_ASSOCIATE"
                     :email="projectAssociate.email"
                     @email-selected="openEmailClient"
                  ></StaffDashboardUser>
               </div>
            </b-col>
         </b-row>
      </div>
      <div
         class="pt-2 pb-4 col position-absolute deadline"
         role="cell"
         :style="`max-width: 150px; line-height: 40px; right:0px; ${
            isFilingDeadlineWithinTwoWeeks ? 'color: #D33C3C;' : ''
         }`"
      >
         {{ formatMonthDay(filingDeadlineMonth, filingDeadlineDay) }}
      </div>
   </div>
</template>

<style scoped lang="scss">
.table-row:hover {
   background-color: #f9fbfe;
}

.client-team > div {
   width: 50%;
}

.project-members > div {
   width: 25%;

   @media (max-width: 1340px) {
      width: 33%;
   }
}

.stage-completion {
   font-size: 0.625rem;
   font-weight: 700;
}

.studies-wrapper > :first-child {
   padding-top: 1.25rem;
}

.icon {
   cursor: pointer;
   border-radius: 100%;
   font-size: 1rem;
   margin-right: 21px;
   background-color: $stage-color-default;
   color: $white;
   border: 5px solid $stage-color-default;

   &--open {
      background-color: $primary;
      color: $white;
      border: 5px solid $primary;
   }
}

.label {
   font-weight: 200;
   font-size: 0.625rem;
}

.description {
   font-weight: 500;
   font-size: 0.625rem;
}

.deadline {
   font-size: 0.75rem;
}

.no-open-study {
   font-size: 0.75rem;
   color: $stage-color-default;
}

.view-client-details {
   font-size: 0.625rem;
   width: 10rem;
}

.team-label {
   font-weight: 700;
   font-size: 0.75rem;
}

/* Align text and loader on the same line */
.loading-container {
   display: inline-flex;
   align-items: center;
   gap: 8px; /* Spaces the loader away from the text */
}

/* Optional text styling */
.loading-text {
}

/* Create and animate the loading wheel */
.loading-spinner {
   width: 16px;
   height: 16px;
   border: 2px solid transparent;
   border-top-color: #007bff; /* Accent color for the spinning indicator */
   border-right-color: #007bff;
   border-radius: 50%;
   animation: spin 0.8s linear infinite;
}

/* Rotation logic */
@keyframes spin {
   to {
      transform: rotate(360deg);
   }
}
</style>
